import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from "moment-timezone";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Step,
    StepLabel,
    Stepper,
    Typography
} from '@mui/material';

import { CREATE_BOOKING_ON_BEHALF_OF, FIND_BOOKABILITY_WITH_FILTER, LOOKUP_FILTERED_BOOKINGS } from '../../queries/GraphQLQueries';
import CustomAlert from "../CustomAlert";
import DisplayNewBookingDetails from "./DisplayNewBookingsDetails";
import DisplayRebookingDetails from "./DisplayRebookingDetails";
import { useDialogNavigation } from "../../hooks/useDialogNavigation";

const STEP_1_PRECHECK_PARAMETERS = "precheckDetailsStep";
const STEP_2_SELECT_TIMESLOTS = "selectTimeslotStep";
const STEP_3_REVIEW_BOOKING = "reviewStep";

export default function CreateBookingDialog() {

    const handleCancel = useDialogNavigation();
    const { state } = useLocation();
    const { selectedChildExternalId, selectedChildName, contentIds, contentName, currentServiceTypeKeycode, currentServiceType } = state || {};

    const startTimeRange = moment().add(1, 'minute').endOf('minute');
    const endTimeRange = moment().add(10, 'days').endOf('day');

    const [availableTimeslots, setAvailableTimeslots] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedTimeslot, setSelectedTimeslot] = useState();
    const [enableNextButton, setEnableNextButton] = useState(true);
    const [bookingStatus, setBookingStatus] = useState(null);
    const [reasonCode, setReasonCode] = useState(null);
    const [bookingId, setBookingId] = useState(null);

    const [findBookability, { loading, error, data }] = useLazyQuery(FIND_BOOKABILITY_WITH_FILTER);
    const [createBookingMutation, { data: createBookingData, loading: bookingResponseLoading, error: bookingError }] = useMutation(CREATE_BOOKING_ON_BEHALF_OF, {
        refetchQueries: [{query: LOOKUP_FILTERED_BOOKINGS}, 'GetFilteredBookings']
    });

    const handleCloseCustomAlert = () => {
        setAlertMessage(null);
    };

    const bookingSteps = [
        {
            name: STEP_1_PRECHECK_PARAMETERS,
            label: 'Booking Details',
            description: 'Check child and content details for re-booking:',
            body: <DisplayRebookingDetails
                showTimeslots={false}
                selectedChildName={selectedChildName}
                currentServiceType={currentServiceType}
                contentName={contentName}
                availableTimeslots={availableTimeslots}
                selectedTimeslot={selectedTimeslot}
                setSelectedTimeslot={setSelectedTimeslot}
            />
        },
        {
            name: STEP_2_SELECT_TIMESLOTS,
            label: 'Choose Timeslot',
            description: 'Choose an upcoming timeslot:',
            body: <DisplayRebookingDetails
                showTimeslots={true}
                selectedChildName={selectedChildName}
                currentServiceType={currentServiceType}
                contentName={contentName}
                availableTimeslots={availableTimeslots}
                selectedTimeslot={selectedTimeslot}
                setSelectedTimeslot={setSelectedTimeslot}
            />,
        },
        {
            name: STEP_3_REVIEW_BOOKING,
            label: 'Confirmation',
            description: 'Review final booking details:',
            body: <DisplayNewBookingDetails
                selectedChildName={selectedChildName}
                currentServiceType={currentServiceType}
                bookingStatus={bookingStatus}
                selectedTimeslot={selectedTimeslot}
                contentName={contentName}
                reasonCode={reasonCode}
            />
        }
    ];

    useEffect(() => {
        setEnableNextButton(true);

        if (bookingSteps[activeStep].name === STEP_2_SELECT_TIMESLOTS) {
            fetchBookability();

            if (selectedTimeslot == null) {
                setEnableNextButton(false);
            }
        } else if (bookingSteps[activeStep].name === STEP_3_REVIEW_BOOKING) {
            executeCreateBookingMutation();
        }
    }, [activeStep, selectedTimeslot]);

    const isLastStep = activeStep === (bookingSteps.length - 1);
    const stepDescription = bookingSteps[activeStep].description;
    const stepBody = bookingSteps[activeStep].body;
    const cancelButtonText = isLastStep ? 'Close' : 'Cancel';
    const actionButtonText = bookingSteps[activeStep].name === STEP_2_SELECT_TIMESLOTS ? 'Book' : 'Next';

    const fetchBookability = () => {
        findBookability({
            variables: {
                from: startTimeRange,
                to: endTimeRange,
                serviceTypeKeyCodes: [currentServiceTypeKeycode],
                contentIds: contentIds
            }
        }).then((result) => {
            if(result.data?.bookability) {
                handleBookabilityResponse(result.data.bookability);
            }
            
        }).catch((e) => {
            setAlertMessage({ children: "Error fetching timeslots " + e.message, severity: 'error' });
            console.log("error " + e)
        });
    }

    const handleBookabilityResponse = (bookabilityResponse) => {
        const availableTimeslots = bookabilityResponse.reduce((timeslots, item) => {
            const filteredPeriods = item.bookablePeriods.filter(
                (period) => period.freeSeatsCount > 0 || period.freePeriodsCount > 0
            );
            return [...timeslots, ...filteredPeriods.map((period) => period.from)];
        }, []);

        setAvailableTimeslots(availableTimeslots);
    }

    const executeCreateBookingMutation = () => {
        createBookingMutation({
            variables: {
                externalChildId: selectedChildExternalId,
                contentIds: contentIds,
                lessonTime: selectedTimeslot,
                serviceTypeKeycode: currentServiceTypeKeycode
            }
        }).then((result) => {
            if (result?.data.createBookingOnBehalfOf) {
                setBookingStatus(result.data.createBookingOnBehalfOf.status);
                setBookingId(result.data.createBookingOnBehalfOf.id);
                setReasonCode(result.data.createBookingOnBehalfOf.reasonCode);
            }
        }).catch((e) => {
            setAlertMessage({ children: e?.message, severity: 'error' });
        });
    }

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        setSelectedTimeslot(null);
    };

    return <>
        <Dialog open={true} onClose={handleCancel} disableRestoreFocus>
            <DialogTitle>Create Booking</DialogTitle>
            <DialogContent>
                <Box sx={{ width: '100%' }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {bookingSteps.map((step, index) => {
                            return (
                                <Step key={step.label} >
                                    <StepLabel>{index + 1}) {step.label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    <br></br>
                    <React.Fragment>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ mt: 2, mb: 1 }}> {stepDescription} </Typography>
                        </Box>

                        <br></br>
                        <Box sx={{ flex: '1 1 auto' }} >{stepBody}</Box>

                        <Typography variant="body1" sx={{ mt: 2 }}>
                            * Please note that by making this booking, it will <strong>not</strong> use a token from the member’s account.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                            {
                              !isLastStep
                              &&
                              <Button disabled={activeStep === 0} onClick={handleBack} sx={{mr: 1}} variant="contained"> Back </Button>
                            }
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Button onClick={handleCancel} variant="contained" sx={{mr:"0.5rem"}}>{cancelButtonText}</Button>
                            {
                              !isLastStep
                              &&
                              <Button onClick={handleNext} disabled={!enableNextButton} variant="contained">{actionButtonText}</Button>
                            }
                        </Box>
                    </React.Fragment>
                </Box>
            </DialogContent>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || bookingResponseLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Dialog>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>;
}